import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {AuthService} from './service/auth.service';
import {MfaService} from './service/mfa.service';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {StorageService} from '../../core/services/storage.service';
import {Router} from '@angular/router';
import {STORAGE_NAME} from '../../core/constant/system.constants';
import {RecaptchaComponent, RecaptchaErrorParameters} from 'ng-recaptcha';
import {ControlInput, SliderCaptchaComponent, VertifyQuery} from '../../shared/component/slider-captcha/slider-captcha.component';
import {NzModalService} from 'ng-zorro-antd/modal';
import {MfaOtpComponent} from '../../shared/component/mfa-otp/mfa-otp.component';
import {MfaFirstLoginComponent} from '../../shared/component/mfa-first-login/mfa-first-login.component';
import {UrlConstant} from './constant/url.class';
import * as moment from 'moment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  passwordVisible = false;
  isLoading = false;
  inValidCaptcha = false;
  inValidLogin = false;
  captchaResponse: any;
  numberLoginFailed = StorageService.get(STORAGE_NAME.NUMBER_LOGIN_FAILED) ? 0 : StorageService.get(STORAGE_NAME.NUMBER_LOGIN_FAILED);
  validateForm!: FormGroup;
  mobileWidth = 993;
  isMobile: any;
  messageError: string;
  @ViewChild('captchaElem') captchaElem: RecaptchaComponent;

  @ViewChild(SliderCaptchaComponent, {static: true})
  slide: SliderCaptchaComponent;
  public controlInput: ControlInput;
  private query: VertifyQuery;

  private captchaId;

  constructor(
    private router: Router,
    private authService: AuthService,
    private mfaService: MfaService,
    private toastrService: ToastrService,
    private translate: TranslateService,
    private modalService: NzModalService,
    private fb: FormBuilder,
  ) {
    this.controlInput = new ControlInput(
      UrlConstant.API_VERSION + '/slider-captcha/get',
      UrlConstant.API_VERSION  + '/slider-captcha/vertify',
      false
    );
    if (window.innerWidth < this.mobileWidth) {
      this.isMobile = true;
    }
  }

  ngOnInit(): void {
    this.validateForm = this.fb.group({
      username: [null, [Validators.required]],
      password: [null, [Validators.required]],
      remember: [true],
      recaptcha: ['']
    });
  }

  submitForm(): void {
    if (this.validateForm.valid) {
      this.login();
    } else {
      Object.values(this.validateForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({onlySelf: true});
        }
      });
    }
  }

  public resolved(captchaResponse: string): void {
    this.captchaResponse = captchaResponse;
  }

  public onError(errorDetails: RecaptchaErrorParameters): void {
    console.log(`reCAPTCHA error encountered; details:`, errorDetails);
  }

  login() {
    this.isLoading = true;
    this.inValidLogin = false;
    this.messageError = null;
    let body: { [key: string]: string } = {};
    body.username = this.validateForm.get('username').value;
    body.password = this.validateForm.get('password').value;
    this.authService.login(body).subscribe(res => {
      this.isLoading = false;
      // ── Trường hợp BE yêu cầu xác thực 2FA ──────────────────
      if (res && res.messageCode === 'MFA_REQUIRED') {
        const mfaToken = res.data?.mfaToken;
        this.openMfaOtpModal(mfaToken);
        return;
      }
      // ── First-login: Admin bật 2FA hộ – hiển thị QR + hướng dẫn ─
      if (res && res.messageCode === 'MFA_FIRST_LOGIN') {
        const { firstLoginToken, qrCodeUrl, secretKey } = res.data ?? {};
        this.openMfaFirstLoginModal(firstLoginToken, qrCodeUrl, secretKey);
        return;
      }
      // ── Đăng nhập thành công (không có 2FA) ──────────────────
      if (res && res.access_token) {
        this.authService.saveStorage(res);
        this.router.navigate(['']);
      } else {
        this.messageError = 'Hệ thống đang bận. Vui lòng thử lại sau.';
      }
    }, error => {
      this.isLoading = false;
      switch (error.messageCode) {
        case 'USER_NOT_EXIST':
          this.messageError = 'Tài khoản không tồn tại';
          break;

        case 'WRONG_PASSWORD':
          this.messageError = 'Mật khẩu không đúng.';
          if (error.data?.countTry < 7) {
            const countTry = error.data?.countTry ? ('Bạn còn ' + error.data.countTry + ' lần thử.') : '';
            this.captchaId = error.data?.captchaId;
            this.messageError += countTry;
          }
          break;

        case 'USER_LOCKED':
          this.messageError = 'Tài khoản đã bị khóa ';
          const lockTime = (error.data?.lock && error.data?.lockLimitRange)
            ? (new Date(error.data.lock).getTime() + 60 * (error.data.lockLimitRange + 1) * 1000)
            : null;
          this.messageError += lockTime ? 'đến ' + moment(lockTime).format('hh:mm') : '';
          break;

        case 'REQUIRED_CAPTCHA':
          console.log('Required captcha');
          this.showCaptcha = true;
          const modal = this.modalService.create({
            nzTitle: 'Xác minh',
            nzFooter: null,
            nzCentered: true,
            nzWidth: 'auto',
            nzContent: SliderCaptchaComponent,
            nzComponentParams: {
              controlInput: this.controlInput,
              username: this.validateForm.get('username').value
            }
          });
          modal.afterClose.subscribe(success => {
            if (success) { this.login(); }
          });
          break;

        default:
          this.messageError = error?.message ?? 'Hệ thống đang bận. Vui lòng thử lại sau.';
          break;
      }
    });
  }

  /** Mở modal nhập OTP cho luồng đăng nhập có 2FA */
  private openMfaOtpModal(mfaToken: string): void {
    const modal = this.modalService.create({
      nzTitle: null,
      nzFooter: null,
      nzCentered: true,
      nzWidth: 420,
      nzClosable: false,
      nzMaskClosable: false,
      nzContent: MfaOtpComponent,
      nzComponentParams: {mfaToken}
    });
    modal.afterClose.subscribe(result => {
      if (result?.expired) {
        // mfaToken hết hạn → reset form để user login lại
        this.messageError = 'Phiên xác thực đã hết hạn. Vui lòng đăng nhập lại.';
      }
    });
  }

  /**
   * Mở modal hướng dẫn + QR cho user lần đầu đăng nhập sau khi Admin bật 2FA hộ.
   * Hiển thị 2 bước: (1) quét QR, (2) nhập OTP xác nhận → cấp token & vào app.
   */
  private openMfaFirstLoginModal(firstLoginToken: string, qrCodeUrl: string, secretKey: string): void {
    const modal = this.modalService.create({
      nzTitle: null,
      nzFooter: null,
      nzCentered: true,
      nzWidth: 500,
      nzClosable: false,
      nzMaskClosable: false,
      nzContent: MfaFirstLoginComponent,
      nzComponentParams: {firstLoginToken, qrCodeUrl, secretKey}
    });
    modal.afterClose.subscribe(result => {
      if (result?.expired) {
        this.messageError = 'Phiên thiết lập 2FA đã hết hạn (10 phút). Vui lòng đăng nhập lại.';
      }
    });
  }

  checkShowRecaptcha() {
    // this.inValidLogin = true;
    // this.inValidCaptcha = false;
    // this.isLoading = false;
    // StorageService.set(STORAGE_NAME.NUMBER_LOGIN_FAILED, this.numberLoginFailed);
    // if (this.numberLoginFailed > 4) {
    //   this.validateForm.controls.recaptcha.addValidators(Validators.required);
    //   this.captchaElem?.reset();
    // }
  }

  showCaptcha

  successMatch( query: VertifyQuery) {
    console.log(query);
    this.query = query;
  }

}
